import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../config";
import { getAuthItem } from "../utils/auth";

function decodeToken() {
  try {
    const token = getAuthItem("token");
    if (!token || token === "null" || token === "undefined") return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

function getAuthHeaders() {
  const token =
    getAuthItem("authToken") || getAuthItem("token");
  if (!token || token === "null" || token === "undefined" || !String(token).trim()) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

export default function useClientSignoffStats() {
  const tokenPayload = useMemo(() => decodeToken(), []);
  const userType = String(tokenPayload?.type || "").toLowerCase();
  const tokenClientId = tokenPayload?.client_id
    ? String(tokenPayload.client_id)
    : "";
  const isContactUser = userType === "contact" || userType === "supervisor";

  const [clients, setClients] = useState([]);
  const [clientIds, setClientIds] = useState(() => {
    if (tokenClientId) return [String(tokenClientId)];
    return [];
  });
  const [clientsReady, setClientsReady] = useState(!isContactUser);
  const [loading, setLoading] = useState(!isContactUser);
  const [initialized, setInitialized] = useState(false);
  const [clientShiftCounts, setClientShiftCounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasSignoffAccess, setHasSignoffAccess] = useState(false);
  const autoSelectDoneRef = useRef(false);
  const fetchGenerationRef = useRef(0);

  const effectiveClientIds = useMemo(() => {
    if (isContactUser) {
      const selected = clientIds.map(String).filter(Boolean);
      if (selected.length) return selected;
      return clients.map((c) => String(c.id)).filter(Boolean);
    }
    if (tokenClientId) return [String(tokenClientId)];
    const stored = getAuthItem("clientId");
    if (stored && stored !== "null") return [stored];
    return [];
  }, [clientIds, clients, isContactUser, tokenClientId]);

  const effectiveClientIdsKey = effectiveClientIds.join(",");

  const loadClients = useCallback(async () => {
    if (!isContactUser) {
      setClientsReady(true);
      return;
    }
    try {
      const res = await fetch(
        `${BASE_URL}/api/contact/assigned-clients?search=`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );
      const json = await res.json();
      const list = (json.data || []).map((c) => ({
        id: c.id ?? c.clientId,
        name: c.name ?? c.clientName,
      }));
      setClients(list);
      if (!autoSelectDoneRef.current && list.length === 1) {
        autoSelectDoneRef.current = true;
        setClientIds([String(list[0].id)]);
      }
    } catch {
      setClients([]);
    } finally {
      setClientsReady(true);
    }
  }, [isContactUser]);

  const fetchSignoffStats = useCallback(
    async ({ silent = false } = {}) => {
      if (!clientsReady) return;

      if (!effectiveClientIdsKey) {
        if (!silent) {
          setClientShiftCounts([]);
          setTotal(0);
          setHasSignoffAccess(false);
          setInitialized(true);
          setLoading(false);
        }
        return;
      }

      const generation = ++fetchGenerationRef.current;
      if (!silent) {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams();
        params.set("client_ids", effectiveClientIdsKey);
        const res = await fetch(
          `${BASE_URL}/api/client/schedule/signoff-stats?${params.toString()}`,
          { credentials: "include", headers: { ...getAuthHeaders() } },
        );
        const json = await res.json();
        if (generation !== fetchGenerationRef.current) return;

        setClientShiftCounts(
          Array.isArray(json.clientShiftCounts) ? json.clientShiftCounts : [],
        );
        setTotal(Number(json.total) || 0);
        setHasSignoffAccess(!!json.hasSignoffAccess);
        setInitialized(true);
      } catch {
        if (generation !== fetchGenerationRef.current) return;
        if (!silent) {
          setClientShiftCounts([]);
          setTotal(0);
          setHasSignoffAccess(false);
          setInitialized(true);
        }
      } finally {
        if (generation === fetchGenerationRef.current && !silent) {
          setLoading(false);
        }
      }
    },
    [clientsReady, effectiveClientIdsKey],
  );

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (!clientsReady) return;
    if (isContactUser && !effectiveClientIdsKey) return;
    fetchSignoffStats({ silent: false });
  }, [clientsReady, effectiveClientIdsKey, isContactUser, fetchSignoffStats]);

  useEffect(() => {
    if (!clientsReady || !initialized) return;

    const onFocus = () => {
      if (!effectiveClientIdsKey) return;
      fetchSignoffStats({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [clientsReady, initialized, effectiveClientIdsKey, fetchSignoffStats]);

  const anyTimesheetReviewRequired = useMemo(
    () => clientShiftCounts.some((row) => row.timesheetReviewRequired),
    [clientShiftCounts],
  );

  const showPanel =
    hasSignoffAccess || (!initialized && !!effectiveClientIdsKey);

  return {
    loading,
    initialized,
    clientsReady,
    clientShiftCounts,
    total,
    hasSignoffAccess,
    showPanel,
    anyTimesheetReviewRequired,
    refresh: () => fetchSignoffStats({ silent: true }),
  };
}
